import { AUTOMATED_LANDING_FINGERPRINT } from "@repo/db/constants";
import { z } from "zod";

/**
 * Lifecycle of an outbound campaign's recruitment link, stored in
 * `channelDetails.lifecycle`. DRAFT = prepared (Smartlead campaign + sequence +
 * schedule exist) but no leads uploaded and not sending; LAUNCHED = leads
 * uploaded and (in production) sending. Shared so the launch tasks that write
 * it and the review UI that reads it can't drift on a bare string literal.
 */
export const OUTBOUND_LIFECYCLE = {
  DRAFT: "DRAFT",
  LAUNCHED: "LAUNCHED",
} as const;
export type OutboundLifecycle =
  (typeof OUTBOUND_LIFECYCLE)[keyof typeof OUTBOUND_LIFECYCLE];

/**
 * How long a launch's claim on a draft (`channelDetails.launchStartedAt`, the
 * first thing launch-outbound-draft writes) is believed. Long enough for an
 * upload of SMARTLEAD_MAX_LEADS_PER_CAMPAIGN leads, short enough that a launch
 * that died does not strand the draft.
 */
export const OUTBOUND_LAUNCH_CLAIM_WINDOW_MS = 30 * 60_000;

/** Whether a draft's launch is running, so its leads and copy must not change. */
export function isOutboundLaunchInFlight(
  launchStartedAt: unknown,
  now: number = Date.now(),
): boolean {
  if (typeof launchStartedAt !== "string") {
    return false;
  }
  const startedAt = Date.parse(launchStartedAt);
  return (
    Number.isFinite(startedAt) &&
    now - startedAt < OUTBOUND_LAUNCH_CLAIM_WINDOW_MS
  );
}

/**
 * What built the audience behind an outbound campaign, stored in
 * `channelDetails.source` by the BYO prepare task and stamped onto every
 * `OutboundLead.source` that task writes.
 *
 * `internal-leads` is a study's own lot, assembled on the Leads tab out of the
 * pool Terac already paid for. It is the only source that tab renders: a
 * vendor-sourced campaign and a hand-uploaded CSV (which stamps `csv`) are a
 * different audience with a different owner, and they stay on Overview, where
 * every channel of a study is reviewable. Shared so the task that writes the
 * field and the surfaces that read it can't drift on a bare string literal.
 */
export const INTERNAL_LEADS_SOURCE = "internal-leads";

/**
 * One bring-your-own-leads lead after client-side CSV column mapping. Only
 * `email` is required; the rest are optional enrichment. Shared by the router
 * input, the prepare use-case, and the Trigger task payload so the shape can't
 * drift across the three layers.
 */
export const byoLeadSchema = z.object({
  email: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  companyName: z.string().optional(),
  companyDomain: z.string().optional(),
  jobTitle: z.string().optional(),
  linkedinUrl: z.string().optional(),
  location: z.string().optional(),
  // Exports often split the city and the country across two columns. Mapped
  // separately so the country is available for schedule resolution
  // (resolveOutboundTimezone) even when `location` is only a city.
  country: z.string().optional(),
  // Carried so a cold caller has something to dial. Outbound itself never
  // sends it anywhere: it is not part of the Smartlead upload payload.
  phone: z.string().optional(),
});
export type ByoLead = z.infer<typeof byoLeadSchema>;

// Prospects to email per desired response. Cold outbound converts at roughly
// 2% emailed-to-completed, so ~50 leads per target response. Tune this single
// knob to change campaign sizing.
export const OUTBOUND_LEAD_MULTIPLIER = 50;

/**
 * Per-campaign hard ceiling on prospects: a cost/sanity bound, NOT a Smartlead
 * or deliverability limit. Each searched lead costs Exa Agent compute plus an
 * email lookup, so this caps per-campaign spend; an upload is capped to it
 * before the suppression query and the Trigger payload. Deliverability is
 * governed separately by the per-mailbox daily limit across the warmed pool,
 * and the upload itself is batched (Smartlead caps lead imports per request).
 */
export const SMARTLEAD_MAX_LEADS_PER_CAMPAIGN = 2500;

/**
 * New leads entering a single campaign's sequence per day. NOT the binding
 * constraint on total volume: the real governor is the pool of warmed mailboxes
 * (~900 x 25/day = ~22,500 sends/day, i.e. ~7,500 new leads/day across ALL
 * campaigns since each lead gets 3 emails). This per-campaign value just paces
 * one campaign's fill when the pool has spare capacity; set to
 * SMARTLEAD_MAX_LEADS_PER_CAMPAIGN so one campaign can drain its whole allowed
 * load in a day when the pool has room, without the per-campaign cap becoming
 * the bottleneck. Even then a 2500-lead campaign is only ~8 first-touches per
 * mailbox across the pool (well under 25/day). Raise total capacity by adding
 * mailboxes, not this number.
 */
export const SMARTLEAD_MAX_NEW_LEADS_PER_DAY = 2500;

/**
 * Minimum age (days since warmup started) before a Smartlead mailbox is eligible
 * to send real outbound. Smartlead warms new mailboxes for ~2 weeks and advises
 * against production sending until then; using a day-0 domain for cold outreach
 * torches its reputation. A mailbox warmed outside Smartlead has no Smartlead
 * warmup start, so its age counts from the day it was added to Smartlead.
 * Campaigns attach every connected mailbox in the sending pool, so this is the
 * single gate that keeps unwarmed accounts out of the sending rotation. The
 * check is self-healing: a mailbox auto-joins the pool the day it crosses this
 * threshold, no code or config change needed.
 */
export const SMARTLEAD_MIN_WARMUP_DAYS = 14;

/**
 * Smartlead tag on the mailboxes replacing the burned Mailreef fleet: once one
 * is warm, only tagged mailboxes send.
 */
export const OUTBOUND_SENDING_POOL_TAG = "pool:inboxkit";

/**
 * Days after any campaign emails a person before another campaign may. Sixty
 * holds anyone to about six cold campaigns a year, whatever number of studies
 * happen to want them.
 */
export const OUTBOUND_CONTACT_COOLDOWN_DAYS = 60;

/**
 * When the outbound bounce guard trips a live campaign. Every campaign sends
 * from one shared mailbox pool, so a campaign bouncing past either rate spends
 * reputation every other study depends on.
 */
// Below 100 emails, one or two dead addresses decide either rate.
export const OUTBOUND_BOUNCE_GUARD_MIN_SENT = 100;
// Recipient bounces measure the list: Smartlead's platform median is 1.54%, and
// its worst quarter of campaigns starts at 3.21%.
export const OUTBOUND_BOUNCE_GUARD_RECIPIENT_RATE = 0.03;
// Sender bounces measure our mailboxes being refused (reputation blocks), which
// carry a baseline no list can fix, so they get a wider margin.
export const OUTBOUND_BOUNCE_GUARD_SENDER_RATE = 0.05;

export interface OutboundBounceCounts {
  sentCount: number;
  bounceCount: number;
  senderBounceCount: number;
}

export interface OutboundBounceReading {
  recipientBounceRate: number;
  senderBounceRate: number;
  tripped: boolean;
}

/**
 * The bounces on the recipient's side: the address does not exist. Smartlead's
 * `bounce_count` includes the sender-side ones (our mailbox was refused), so
 * they are subtracted rather than added.
 */
export function countRecipientBounces(counts: OutboundBounceCounts): number {
  return Math.max(counts.bounceCount - counts.senderBounceCount, 0);
}

/**
 * Split a campaign's bounces into the recipient side and the sender side, and
 * say whether either crossed its rate on a big enough sample.
 */
export function readOutboundBounces(
  counts: OutboundBounceCounts,
): OutboundBounceReading {
  if (counts.sentCount <= 0) {
    return { recipientBounceRate: 0, senderBounceRate: 0, tripped: false };
  }
  const recipientBounceRate = countRecipientBounces(counts) / counts.sentCount;
  const senderBounceRate = counts.senderBounceCount / counts.sentCount;
  const tripped =
    counts.sentCount >= OUTBOUND_BOUNCE_GUARD_MIN_SENT &&
    (recipientBounceRate >= OUTBOUND_BOUNCE_GUARD_RECIPIENT_RATE ||
      senderBounceRate >= OUTBOUND_BOUNCE_GUARD_SENDER_RATE);
  return { recipientBounceRate, senderBounceRate, tripped };
}

/**
 * Days a MillionVerifier verdict is reused before the address is paid for
 * again. Lists decay by about 2% a month, so a 90-day-old verdict still holds
 * for nearly every address.
 */
export const OUTBOUND_EMAIL_VERIFICATION_TTL_DAYS = 90;

/**
 * Largest share of a lot's sent leads that may be catch-all addresses. A
 * catch-all domain accepts mail for any mailbox, so no verifier can say whether
 * the person exists; the cap bounds the bounces they can add.
 */
export const OUTBOUND_CATCH_ALL_MAX_SHARE = 0.1;

/**
 * Whether an outbound landing came from an automated renderer rather than a
 * person (the rendering fleet's `AUTOMATED_LANDING_FINGERPRINT`, in
 * @repo/db/constants), so the funnel's landing denominator can exclude it.
 *
 * Applied at read time against the raw fingerprint on the row, never stored as a
 * flag: the fingerprint is a heuristic about someone else's infrastructure and
 * will need revising, and a stored boolean would need a migration and a backfill
 * to revise. The outbound-lead repository matches the same fingerprint in SQL
 * (the arm funnel, the landing fingerprint write); a change here goes there too.
 *
 * A lead that produced a submission is never classified automated, whatever its
 * fingerprint. Converting is proof of a person, and without this the rule costs
 * a real conversion for every genuine Pacific-timezone visitor on a 1080p
 * monitor, one of the 17 in the measurement window.
 *
 * A landing with no fingerprint is treated as human: either the visitor was
 * already authenticated (a session proves a person, and that path redirects
 * before any client component mounts) or the beacon did not run. Both are
 * better counted than silently dropped.
 */
export function isAutomatedLanding(lead: {
  landingScreen: string | null;
  landingTimezone: string | null;
  submissionId: string | null;
}): boolean {
  if (lead.submissionId) {
    return false;
  }
  return (
    lead.landingScreen === AUTOMATED_LANDING_FINGERPRINT.screen &&
    lead.landingTimezone === AUTOMATED_LANDING_FINGERPRINT.timezone
  );
}

/**
 * How someone takes part in a one-time study, for the cold email: a call they
 * book (a moderated study, which a scheduled-call task marks) or work they do on
 * their own (an unmoderated test, a survey, an upload). Hourly and per-task work
 * is a project the participant fits around their own schedule, whatever its plan
 * holds, so the email does not read this for it.
 *
 * Nothing finer is read. The external-link task goes to any site (Maze, Simile,
 * Google Forms, other vendors' AI interviews, prototypes), so its link names no
 * format the email could promise; letting the copy model guess one is what
 * advertised self-paced work as "a video call with the research team".
 */
export type OutboundSessionFormat = "call" | "self_paced";

/**
 * Signing, joining Slack, installing a tracker and passing an assessment are
 * steps toward the paid work, never the work itself.
 */
const NON_WORK_TASK_TYPES = new Set([
  "AGREEMENT",
  "E_SIGNATURE",
  "JOIN_SLACK",
  "SLACK_HANDOFF",
  "INSTALL_TIME_TRACKER",
  "CALIBRATION",
]);

/**
 * The format of a study's task plan, or null when the plan holds no paid work
 * to read it from. Null makes the email say nothing about how or when.
 */
export function resolveSessionFormat(
  taskPlan: { taskType?: string | null }[] | null | undefined,
): OutboundSessionFormat | null {
  const work = (taskPlan ?? []).filter(
    (task) => task.taskType && !NON_WORK_TASK_TYPES.has(task.taskType),
  );
  if (work.some((task) => task.taskType === "SCHEDULED_INTERVIEW")) {
    return "call";
  }
  return work.length > 0 ? "self_paced" : null;
}

/**
 * Minutes of a study's scheduled call, or null when it books none or gives it
 * no length. The only length the cold email states: other tasks' minutes
 * understated real work (a ~4,000-word written guide planned as 45 minutes).
 */
export function resolveCallDurationMins(
  taskPlan:
    | { taskType?: string | null; durationMinutes?: number | null }[]
    | null
    | undefined,
): number | null {
  const minutes = (taskPlan ?? [])
    .filter((task) => task.taskType === "SCHEDULED_INTERVIEW")
    .reduce((sum, task) => sum + (task.durationMinutes ?? 0), 0);
  return minutes > 0 ? minutes : null;
}

const SETUP_TASK_TYPES = new Set(["INSTALL_TIME_TRACKER", "CALIBRATION"]);

/**
 * Whether a participant has something to install or pass before the paid work,
 * which makes "No preparation is needed" untrue.
 */
export function planRequiresSetup(
  taskPlan: { taskType?: string | null }[] | null | undefined,
): boolean {
  return (taskPlan ?? []).some((task) =>
    SETUP_TASK_TYPES.has(task.taskType ?? ""),
  );
}

/**
 * Smartlead AI reply-category ids (from GET /leads/fetch-categories, stable
 * account-wide). Used by outbound to (a) tell Smartlead which categories its AI
 * should auto-assign, (b) route the leads it marks INTERESTED into the reply
 * that carries the study link and (c) suppress the leads it marks
 * DO_NOT_CONTACT. NOT_INTERESTED is recorded as a reply, not an opt-out: a "no"
 * declines one study, not every future one.
 */
export const SMARTLEAD_LEAD_CATEGORY = {
  INTERESTED: 1,
  MEETING_REQUEST: 2,
  NOT_INTERESTED: 3,
  DO_NOT_CONTACT: 4,
  INFORMATION_REQUEST: 5,
  OUT_OF_OFFICE: 6,
  WRONG_PERSON: 7,
} as const;

/**
 * Category NAMES, for the one Smartlead endpoint that rejects the ids above.
 *
 * The two endpoints disagree and both validate strictly, so they cannot share a
 * representation (live-probed 2026-07-27):
 *   - `POST /campaigns/{id}/settings` `ai_categorisation_options` wants NUMBERS
 *     ("must be a number" on a name).
 *   - `POST /campaigns/{id}/webhooks` `categories` wants STRINGS
 *     ("categories[0] must be a string" on an id, then "Invalid category - X"
 *     if the name is not one of the account's categories).
 */
export const SMARTLEAD_LEAD_CATEGORY_NAME = {
  INTERESTED: "Interested",
  NOT_INTERESTED: "Not Interested",
  DO_NOT_CONTACT: "Do Not Contact",
} as const;

/**
 * UI / Zod bound on the user-supplied target response count. Independent from
 * the provider cap, so changing one does not have to move the other.
 */
export const OUTBOUND_MAX_TARGET_COUNT = 250;

/**
 * Converts a target response count into a prospect count via the lead
 * multiplier, bounded by the per-campaign cap. With the current defaults
 * (50x, cap 2500) a target of 50+ hits the cap; a typical target of 20 yields
 * 1000 leads.
 */
export function leadsFromTargetCount(targetCount: number): number {
  return Math.min(
    targetCount * OUTBOUND_LEAD_MULTIPLIER,
    SMARTLEAD_MAX_LEADS_PER_CAMPAIGN,
  );
}

/**
 * Most one outbound search may spend on Exa, across every run it makes. At
 * the observed $0.035, $0.20 and $0.45 per person with an email, a broad
 * audience fills OUTBOUND_SEARCH_MAX_LEADS for ~$35, a typical one ~500
 * leads and a niche one ~220.
 */
export const OUTBOUND_SEARCH_BUDGET_DOLLARS = 100;

/**
 * Most people one outbound search asks Exa for, whatever the target: ~20
 * responses at OUTBOUND_LEAD_MULTIPLIER. A bigger target runs another
 * campaign rather than one search that spends the whole budget on a broad
 * audience. Uploaded lists keep SMARTLEAD_MAX_LEADS_PER_CAMPAIGN.
 */
export const OUTBOUND_SEARCH_MAX_LEADS = 1000;

/** People one outbound search asks for: the target's lead count, capped. */
export function searchLeadsFromTargetCount(targetCount: number): number {
  return Math.min(leadsFromTargetCount(targetCount), OUTBOUND_SEARCH_MAX_LEADS);
}

/**
 * Plain-language sizing hint shown on the outbound search surfaces: how many
 * people we'll email and how many responses that yields. Shared so the copy
 * and the reply-rate math can't drift between the quick-launch modal and the
 * add-channel form.
 *
 * The response figure is derived from the (capped) lead count, NOT the raw
 * target: the search asks for at most OUTBOUND_SEARCH_MAX_LEADS, so a target
 * above ~20 can't be reached by one campaign. Computing responses back from
 * the leads keeps the promise honest instead of over-stating it.
 */
export function describeOutboundReach(targetCount: number): string {
  const leads = searchLeadsFromTargetCount(targetCount);
  const expectedResponses = Math.round(leads / OUTBOUND_LEAD_MULTIPLIER);
  return `We'll email up to ${leads.toLocaleString()} people to reach ~${expectedResponses.toLocaleString()} responses. The search spends at most $${OUTBOUND_SEARCH_BUDGET_DOLLARS}, so a niche audience comes back smaller.`;
}

/**
 * What the outbound surfaces say once a search campaign is being prepared.
 * Prepare only writes the brief and the email; Exa searches when the draft is
 * launched.
 */
export const OUTBOUND_SEARCH_STARTED_MESSAGE =
  "Preparing the draft. Review the brief and the email, then Launch: Exa finds the people and they're emailed as soon as it finishes.";
